import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import { type BdCreateSchema, type BdQueryAllSchema, type BdQuerySchema, type BdUpdateSchema } from './schemas';

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, BdCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/business-disciplines`,
        payload,
      }),
  });

export function useGetBusinessDisciplines(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['BUSINESS_DISCIPLINES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<BdQueryAllSchema>({
        endpoint: '/api/v1/business-disciplines',
        params: `${apiParamStr}&include=logo_dark,logo_light`,
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['BUSINESS_DISCIPLINES'] });
};

export const useGetBusinessDisciplinesMutation: UseControlledMutation<BdQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['BUSINESS_DISCIPLINES_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<BdQueryAllSchema>({
            endpoint: '/api/v1/business-disciplines',
            params: { search, per_page },
          });
          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetBusinessDiscipline(id: string) {
  return useSuspenseQuery({
    queryKey: ['BUSINESS_DISCIPLINE', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: BdQuerySchema }>({
        endpoint: `/api/v1/business-disciplines/${id}?include=logo_dark,logo_light`,
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: BdQuerySchema }, ApiError, Partial<BdUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/business-disciplines/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['BUSINESS_DISCIPLINE_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/business-disciplines/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
